import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Immutable, { Map } from 'immutable';
import ComponentsSelect from '../types/ComponentsSelect';
import InputBox from '../inputs/InputBox';

import { Field, reduxForm, formValueSelector } from 'redux-form';
import { validateAndDispatch } from '../../helpers/form';
import { createSelector } from 'reselect';
import { ModalActions } from '../../actions/modal';
import { ElementTypeActions } from '../../actions/elementType';
import { RelationshipTypeActions } from '../../actions/relationshipType';

import { getComponent } from '../types/Components';
// import './CreateProjectButton.css';

const modalSelector = (state) => state.modal.get('modals').last();
const typeTypeSelector = (state) => modalSelector(state).getIn(['options', 'type']);
const currentTypeIdSelector = (state) => modalSelector(state).getIn(['options', 'typeId']);
const propertyNameSelector = (state) => modalSelector(state).getIn(['options', 'prop']);
const isLockedSelector = (state) => modalSelector(state).getIn(['options', 'isLocked'], null);
const propOrderSelector = (state) => modalSelector(state).getIn(['options', 'order'], null);
const projectIdSelector = (state) => state.projects.get('currentProjectId');
const elementTypesSelector = (state) => state.elementTypes.get('elementTypes');
const relationshipTypesSelector = (state) => state.relationshipTypes.get('relationshipTypes');
const currentTypeSelector = (state) => {
  const typeType = typeTypeSelector(state);
  const typeId = currentTypeIdSelector(state);
  const type = (typeType === 'elementType') ?
    elementTypesSelector(state) : relationshipTypesSelector(state);
  return type.getIn([typeId]);
};
const propsSelector = (state) => {
  const type = currentTypeSelector(state);
  const propName = propertyNameSelector(state);
  return type.getIn(['props', propName]);
};

const initValuesSelector = (state) => {
  const propVals = propsSelector(state);
  const propName = propertyNameSelector(state);
  const typeOpts = (propVals.has('_typeOptions') &&
    propVals.getIn(['_typeOptions'])) ?
    propVals.getIn(['_typeOptions']).toJSON() : null;

  return (propVals && propName) ? {
    propType: propVals.getIn(['_type']),
    propName: propertyNameSelector(state),
    propValue: (Immutable.Iterable.isIterable(propVals.getIn(['_default'])))
      ? propVals.getIn(['_default']).toJS()
      : propVals.getIn(['_default']),
    propHelpText: propVals.getIn(['_helptext']),
    propComment: propVals.getIn(['_comment']),
    displayValue: propVals.getIn(['_display']),
    typeOptions: typeOpts,
  } : {};
};

const editTypePropertyFormSelector = (state) => {
  const selector = formValueSelector('editTypeProperty');

  return selector(
    state,
    'propType',
    'propName',
    'propValue',
    'propHelpText',
    'propComment',
    'displayValue',
    'typeOptions',
    'readOnly'
  );
};

const EditTypePropertyModalSelector = createSelector(
  [
    typeTypeSelector,
    currentTypeIdSelector,
    projectIdSelector,
    currentTypeSelector,
    propertyNameSelector,
    isLockedSelector,
    propOrderSelector,
    propsSelector,
    editTypePropertyFormSelector,
    initValuesSelector,
  ],
  (
    typeType,
    currentTypeId,
    projectId,
    currentType,
    propertyName,
    isLocked,
    propOrder,
    currentTypeProps,
    editTypePropertyForm,
    initValues,
  ) => ({
    typeType,
    currentTypeId,
    projectId,
    currentType,
    propertyName,
    isLocked,
    propOrder,
    currentTypeProps,
    fields: editTypePropertyForm,
    initialValues: initValues,
  })
);

@connect(EditTypePropertyModalSelector)
@reduxForm({
  form: 'editTypeProperty',
  destroyOnUnmount: true,
})
export default class EditTypePropModal extends Component {
  static propTypes = {
    dispatch: PropTypes.func,
    elementTypes: PropTypes.instanceOf(Map),
  }

  onClose() {
    const { dispatch } = this.props;
    dispatch(ModalActions.hideModal());
  }

  onBack() {
    const { dispatch } = this.props;
    dispatch(ModalActions.backModal());
  }

  submit = (values, dispatch) => {
    const {
      fields: {
        propType,
        propName,
        propValue,
        propHelpText,
        propComment,
        displayValue,
        typeOptions,
        readOnly,
      },
      reset,
      typeType,
      currentTypeId,
      currentType,
      initialValues,
      isLocked,
      propOrder,
    } = this.props;

    let currType = currentType;

    let propObj = Immutable.fromJS({
      _type: propType,
      _default: propValue,
      _display: displayValue,
      _helptext: propHelpText,
      _comment: propComment,
      _readOnly: readOnly,
    });

    if (propOrder) {
      propObj = propObj.set('_order', propOrder);
    }

    if (isLocked) {
      propObj = propObj.set('_lock', true);
    }

    if (typeOptions) {
      propObj = propObj.set('_typeOptions', typeOptions);
    }

    currType = currType.setIn(['props', initialValues.propName], propObj);

    const hasRename = (initialValues.propName !== propName);

    const renamePropObj = {
      prop: initialValues.propName,
      newPropKey: propName,
    };

    const renameKey = (typeType === 'elementType') ? 'elementTypeId' : 'relationshipTypeId';
    renamePropObj[renameKey] = currentTypeId;

    const validation = {
      propName: [{ rule: 'required', label: 'property name' }],
      propType: [{ rule: 'required', label: 'property type' }],
    };

    const renameAction = (typeType === 'elementType') ?
      ElementTypeActions.renameElementTypeProperty(renamePropObj) :
      RelationshipTypeActions.renameRelationshipTypeProperty(renamePropObj);

    const action = (typeType === 'elementType') ?
      ElementTypeActions.updateElementTypeProperties(currType) :
      RelationshipTypeActions.updateRelationshipTypeProperties(currType);

    return validateAndDispatch(
      dispatch,
      action,
      validation,
      values
    ).then(() => {
      // console.log('hasRename', hasRename);
      if (hasRename) {
        dispatch(renameAction);
      }
    }).then(() => {
      reset();
    });
  }

  renderTypeComponent() {
    const {
      fields: {
        propType,
        propName,
        propValue,
      },
      dispatch,
    } = this.props;

    if (!propType) {
      return <div />; // propertyValue = 'string';
    }

    const TypeComponent = getComponent(propType);
    const propertyName = propName || null;

    return (<Field
      name="propValue"
      component={TypeComponent.designer}
      form="editTypeProperty"
      uid="editTypeProperty"
      wrapperClassName="form-group row mb-2"
      labelClassName="col-sm-3 col-form-label"
      containerClassName="col-sm-9"
      propertyKey={propertyName}
      value={propValue}
      dispatch={dispatch}
    />);
  }

  render() {
    const {
      propertyName,
      isLocked,
      dispatch,
      handleSubmit,
      submitting,
    } = this.props;

    return (
      <div className="fs-modal-content">
        <h4>Edit Property: {propertyName}</h4>
        <form>
          <div className="modal-form-wrapper">
            <Field
              component={InputBox}
              autoFocus
              name="propName"
              className="form-control"
              wrapperClassName="form-group row mb-2"
              labelClassName="col-sm-3 col-form-label"
              containerClassName="col-sm-9"
              label="Property Name"
              placeholder="Property Name"
              small="Enter a name for the new property."
              disabled={isLocked}
            />
            <div className="form-group row mb-2">
              <label htmlFor="propType" className="col-sm-3 col-form-label">Type</label>
              <div className="col-sm-9">
                <Field
                  component={ComponentsSelect}
                  name="propType"
                  field="propType"
                  formName="editTypeProperty"
                  dispatch={dispatch}
                  disabled={isLocked}
                />
              </div>
            </div>
            {this.renderTypeComponent()}
            <div className="form-group row mb-2">
              <label htmlFor="propertyHelpText" className="col-sm-3 col-form-label">Help Text</label>
              <div className="col-sm-9">
                <Field
                  component="input"
                  type="text"
                  name="propHelpText"
                  className="form-control"
                  id="propertyHelpTextInput"
                  ref="propertyHelpText"
                  placeholder="Property Help Text"
                />
              </div>
            </div>
            <div className="form-group row mb-2">
              <label htmlFor="propertyComment" className="col-sm-3 col-form-label">Comment</label>
              <div className="col-sm-9">
                <Field
                  component="textarea"
                  name="propComment"
                  className="form-control"
                  id="propertyCommentInput"
                  ref="propertyComment"
                  placeholder="Property Comment"
                />
              </div>
            </div>
            <div className="form-group row mb-2">
              <label className="col-sm-3 col-form-label">Display</label>
              <div className="col-sm-9">
                <Field
                  className="mt-2"
                  name="displayValue"
                  ref="displayValue"
                  component="input"
                  type="checkbox"
                />
              </div>
            </div>
            <div className="form-group row mb-2">
              <label className="col-sm-3 col-form-label">Read Only</label>
              <div className="col-sm-9">
                <Field
                  className="mt-2"
                  name="readOnly"
                  ref="readOnly"
                  component="input"
                  type="checkbox"
                />
              </div>
            </div>
          </div>
          <div className="modal-form-button-wrapper">
            <button
              className="btn btn-primary modal-submit-btn"
              onClick={handleSubmit(this.submit)}
              disabled={submitting}
            >
              {submitting && <i className="fa fa-cog fa-spinner fa-spin"></i>} Save
            </button>
          </div>
        </form>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/modals/EditTypePropertyModal.js